import { useMemo, useCallback, useEffect, useState } from "react"
import { Form, Row, Col, Label, Button, FormFeedback } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { toast } from "react-toastify"
import { useQueryClient } from "@tanstack/react-query"
import VehicleIdSelect from "components/Common/VehicleIdSelect"
import {
  useCreateVehicleFuelUse,
  useEditVehicleFuelUse,
  VEHICLE_FUEL_USE_QUERY_KEY,
} from "hooks/useVehicleFuelUses"
import FormikCreatableField from "components/Common/FormikCreatableField"

const VehicleFuelUsageAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  fuelUsage,
  employees,
  optionLoading,
  totals,
}) => {
  const queryClient = useQueryClient()
  const [mileageSuggestion, setMileageSuggestion] = useState(null)
  const { mutateAsync: createFuelUsage, isPending: createLoading } =
    useCreateVehicleFuelUse()

  const { mutateAsync: editFuelUsage, isPending: editLoading } =
    useEditVehicleFuelUse()

  // Mileage is validated the same way as Add Vehicle Movement: it may not drop
  // below the vehicle's last known reading unless flagged as an odometer fix.
  const validationSchema = Yup.object({
    vehicle_id: Yup.mixed().required("Please select vehicle"),
    uses_by_id: Yup.mixed().required("Please select a staff"),
    fuel_qty: Yup.number()
      .required("Please enter fuel quantity")
      .min(0, "Quantity must be >= 0"),
    mileage: Yup.number()
      .required("Please enter mileage")
      .min(0, "Mileage must be >= 0")
      .test("not-below-last", function (value) {
        const baseline = mileageSuggestion?.value
        if (this.parent.mileage_corrected || baseline == null || value == null) {
          return true
        }
        if (value >= Number(baseline)) {
          return true
        }
        return this.createError({
          message: `Mileage cannot be less than the last record (${Number(
            baseline
          ).toLocaleString()} km). Tick 'Odometer corrected' to allow a lower value.`,
        })
      }),
    uses_date: Yup.date().required("Please select date"),
    remarks: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm, setFieldError }) => {
      const payload = {
        ...values,
        vehicle_id: values.vehicle_id?.value || values.vehicle_id,
      }

      const action = isEdit
        ? editFuelUsage({ id: fuelUsage.id, ...payload })
        : createFuelUsage(payload)
      try {
        const res = await action
        queryClient.invalidateQueries({
          queryKey: [VEHICLE_FUEL_USE_QUERY_KEY, "all"],
          exact: true, // ensures only this exact key is invalidated
        })

        toast.success(
          res?.message ??
            `Fuel Usage ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        // Surface a backend mileage rejection inline
        const mileageError = error?.response?.data?.errors?.mileage?.[0]
        if (mileageError) {
          setFieldError("mileage", mileageError)
        } else {
          toast.error("Failed to save Fuel Usage.")
        }
      }
    },
    [isEdit, fuelUsage, editFuelUsage, createFuelUsage, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      uses_by_id: fuelUsage?.uses_by_id || "",
      vehicle_id: fuelUsage?.vehicle_id || "",
      fuel_qty: fuelUsage?.fuel_qty || "",
      mileage: fuelUsage?.mileage || "",
      mileage_corrected: fuelUsage?.mileage_corrected ?? false,
      uses_date: fuelUsage?.uses_date || "",
      remarks: fuelUsage?.remarks || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  // ✅ Set vehicle_id field when editing
  useEffect(() => {
    setMileageSuggestion(null)
    if (fuelUsage?.vehicle?.id) {
      formik.setFieldValue("vehicle_id", {
        label: `${fuelUsage.vehicle.stock_id} - ${
          fuelUsage.vehicle.full_chassis ?? ""
        }`,
        value: fuelUsage.vehicle.id,
      })
    } else {
      formik.setFieldValue("vehicle_id", null)
    }
  }, [fuelUsage])

  // ✅ Auto-fill mileage from the selected vehicle's last movement, falling
  // back to the vehicle's own recorded mileage when it has no movement history.
  // The resolved value also becomes the baseline for the not-below-last check.
  const handleVehicleSelect = useCallback(
    vehicle => {
      if (!vehicle) {
        setMileageSuggestion(null)
        return
      }

      const hasValue = v => v !== null && v !== undefined && v !== ""

      const fromMovement = hasValue(vehicle.last_movement_mileage)
      const suggested = fromMovement
        ? vehicle.last_movement_mileage
        : vehicle.mileage

      setMileageSuggestion(
        hasValue(suggested)
          ? {
              value: suggested,
              source: fromMovement ? "last movement" : "vehicle record",
            }
          : null
      )

      if (hasValue(suggested)) {
        formik.setFieldValue("mileage", suggested)
      }
    },
    [formik]
  )

  // Live fuel-stock picture: the available pool and what this entry leaves
  // behind. On edit, this record's saved quantity is already deducted from the
  // global remaining, so it is added back to reflect the true available pool.
  const fuelStockInfo = useMemo(() => {
    const remaining = Number(totals?.remaining_fuel_stock ?? 0)
    const originalQty = isEdit ? Number(fuelUsage?.fuel_qty ?? 0) : 0
    const available = remaining + originalQty
    const enteredQty = Number(formik.values.fuel_qty) || 0
    const projected = available - enteredQty

    return {
      available,
      projected,
      isOverdrawn: enteredQty > 0 && projected < 0,
    }
  }, [totals, isEdit, fuelUsage, formik.values.fuel_qty])

  // Distance covered since the baseline reading (last movement / vehicle
  // record) — shows the user the real km run for the entered mileage.
  const mileageRunInfo = useMemo(() => {
    const baseline = mileageSuggestion?.value
    const entered = Number(formik.values.mileage)

    if (
      baseline == null ||
      formik.values.mileage === "" ||
      !Number.isFinite(entered)
    ) {
      return null
    }

    const distance = entered - Number(baseline)

    return {
      distance,
      source: mileageSuggestion.source,
      isNegative: distance < 0,
    }
  }, [mileageSuggestion, formik.values.mileage])

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Fuel Usage"}${
      loading ? "..." : ""
    }`
  }, [isEdit, editLoading, createLoading])

  const isSubmitDisabled = useMemo(
    () => optionLoading || editLoading || createLoading,
    [optionLoading, editLoading, createLoading]
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Fuel Usage" : "Add Fuel Usage"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <Row className="mb-3">
          <Col sm={12} className="mb-3">
            <VehicleIdSelect
              formik={formik}
              onVehicleSelect={handleVehicleSelect}
            />
          </Col>

          <Col sm={6} className="mb-3">
            <Label htmlFor="fuel_qty">Quantity (L)</Label>
            <InputField
              type="number"
              name="fuel_qty"
              id="fuel_qty"
              value={formik.values.fuel_qty}
              onChange={formik.handleChange}
              invalid={formik.touched.fuel_qty && !!formik.errors.fuel_qty}
              loading={optionLoading}
            />
            {formik.touched.fuel_qty && formik.errors.fuel_qty && (
              <FormFeedback type="invalid">
                {formik.errors.fuel_qty}
              </FormFeedback>
            )}
            <small className="text-muted d-block mt-1">
              Stock available:{" "}
              {fuelStockInfo.available.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}{" "}
              L
            </small>
            <small
              className={`d-block ${
                fuelStockInfo.isOverdrawn
                  ? "text-danger fw-semibold"
                  : "text-success"
              }`}
            >
              {fuelStockInfo.isOverdrawn
                ? `Exceeds available stock by ${Math.abs(
                    fuelStockInfo.projected
                  ).toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })} L`
                : `Remaining after this entry: ${fuelStockInfo.projected.toLocaleString(
                    undefined,
                    { maximumFractionDigits: 2 }
                  )} L`}
            </small>
          </Col>
          <Col sm={6} className="mb-3">
            <Label htmlFor="mileage">Last Mileage</Label>
            <InputField
              type="number"
              name="mileage"
              id="mileage"
              value={formik.values.mileage}
              onChange={e => {
                // Validate mileage live while typing
                formik.setFieldValue("mileage", e.target.value, true)
                formik.setFieldTouched("mileage", true, false)
              }}
              onBlur={() => formik.setFieldTouched("mileage", true, true)}
              invalid={formik.touched.mileage && !!formik.errors.mileage}
              loading={optionLoading}
            />
            {formik.touched.mileage && formik.errors.mileage && (
              <FormFeedback type="invalid">
                {formik.errors.mileage}
              </FormFeedback>
            )}
            {mileageSuggestion &&
              !(formik.touched.mileage && formik.errors.mileage) && (
                <small className="text-muted d-block mt-1">
                  Suggested from {mileageSuggestion.source}:{" "}
                  {Number(mileageSuggestion.value).toLocaleString()} km
                </small>
              )}
            {mileageRunInfo && (
              <small
                className={`d-block ${
                  mileageRunInfo.isNegative ? "text-danger" : "text-info"
                }`}
              >
                {mileageRunInfo.isNegative
                  ? `${Math.abs(mileageRunInfo.distance).toLocaleString()} km below the last ${
                      mileageRunInfo.source
                    } reading`
                  : `${mileageRunInfo.distance.toLocaleString()} km run since the last ${
                      mileageRunInfo.source
                    } reading`}
              </small>
            )}
          </Col>

          <Col sm={12} className="mb-3">
            <Button
              type="button"
              size="sm"
              color="secondary"
              outline={!formik.values.mileage_corrected}
              onClick={() =>
                formik.setFieldValue(
                  "mileage_corrected",
                  !formik.values.mileage_corrected,
                  true
                )
              }
            >
              <i
                className={`bx ${
                  formik.values.mileage_corrected
                    ? "bx-check-square"
                    : "bx-square"
                } me-1`}
              />
              Odometer corrected (allow mileage lower than last record)
            </Button>
          </Col>

          <Col sm={6}>
            <FormikCreatableField
              name="uses_by_id"
              label="Usage By"
              formik={formik}
              data={employees}
              placeholder="Select a staff"
            />
            {formik.touched.uses_by_id && formik.errors.uses_by_id && (
              <FormFeedback type="invalid">
                {formik.errors.uses_by_id}
              </FormFeedback>
            )}
          </Col>

          <Col sm={6} className="mb-3">
            <Label htmlFor="date">Usage Date</Label>
            <InputField
              type="datetime-local"
              name="uses_date"
              id="uses_date"
              value={formik.values.uses_date}
              onChange={formik.handleChange}
              invalid={formik.touched.uses_date && !!formik.errors.uses_date}
              loading={optionLoading}
            />
            {formik.touched.uses_date && formik.errors.uses_date && (
              <FormFeedback type="invalid">
                {formik.errors.uses_date}
              </FormFeedback>
            )}
          </Col>
        </Row>

        <div className="mb-3">
          <Label htmlFor="remarks">Remarks</Label>
          <InputField
            type="textarea"
            name="remarks"
            id="remarks"
            value={formik.values.remarks}
            onChange={formik.handleChange}
            invalid={formik.touched.remarks && !!formik.errors.remarks}
            loading={optionLoading}
          />
          {formik.touched.remarks && formik.errors.remarks && (
            <FormFeedback type="invalid">{formik.errors.remarks}</FormFeedback>
          )}
        </div>

        <div className="text-end">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default VehicleFuelUsageAddEdit
